<script setup>
/**
 * @fileoverview ShareDesign component for Custom Mosaics.
 *
 * A "Copy URL" button that copies the design's share link to the clipboard.
 * The link itself is not shown.
 *
 * @component
 */
import { ref } from 'vue';
import { useMosaicStore } from '../../stores/mosaicStore';
import { trackShareClick } from '../../utils/trackingService';
import { LinkIcon, CheckIcon } from '@heroicons/vue/24/outline';

const store = useMosaicStore();

defineProps({
    /** Text shown before the button */
    label: {
        type: String,
        default: 'Share Your Design',
    },
});

/** @type {import('vue').Ref<boolean>} Whether the share URL was recently copied */
const copied = ref(false);

/**
 * Copy the share URL to the clipboard.
 * Uses the Clipboard API when available, falls back to execCommand for older browsers.
 * Tracks the share action for analytics.
 */
async function copyShareUrl() {
    const text = String(store.shareUrl || '');
    try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
            await navigator.clipboard.writeText(text);
        } else {
            const ta = document.createElement('textarea');
            ta.value = text;
            ta.setAttribute('readonly', '');
            ta.style.position = 'absolute';
            ta.style.left = '-9999px';
            document.body.appendChild(ta);
            ta.select();
            const success = document.execCommand('copy');
            document.body.removeChild(ta);
            if (!success) {
                throw new Error('execCommand copy failed');
            }
        }
        copied.value = true;
        setTimeout(() => (copied.value = false), 1500);

        // Track share click with full URL
        trackShareClick(text);
    } catch {
        // Silently fail
    }
}
</script>

<template>
    <div class="flex items-center gap-3">
        <span class="text-sm font-medium text-dryline">{{ label }}</span>
        <button
            type="button"
            class="inline-flex items-center gap-1.5 px-3 py-1.5 border border-dryline text-dryline text-xs uppercase tracking-wide bg-white hover:bg-dryline hover:text-white"
            data-testid="copy-share-url"
            @click="copyShareUrl"
        >
            <CheckIcon v-if="copied" class="w-4 h-4" aria-hidden="true" />
            <LinkIcon v-else class="w-4 h-4" aria-hidden="true" />
            <span>{{ copied ? 'Copied' : 'Copy URL' }}</span>
        </button>
        <span class="sr-only" role="status">{{ copied ? 'Design link copied' : '' }}</span>
    </div>
</template>
